import React, { useState } from 'react';
import { Box, Typography, Avatar, Chip, Card, CardContent, IconButton, CircularProgress, useTheme, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { Download as DownloadIcon, Flag as FlagIcon } from '@mui/icons-material';
import useThemeStyles from '../../theme/useThemeStyles';
import { chatTheme } from '../../theme/chatTheme';
import { getAvatarSrc as getAvatarSrcUtil, DEFAULT_AVATAR } from '../../utils/avatarConstants';
import api from '../../utils/api';

const MessageBubble = ({ message, _currentUserId, userType, chatId, chatContext, currentUserName }) => {
  const _styles = useThemeStyles();
  const theme = useTheme();
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reporting, setReporting] = useState(false);
  const isSent = message.senderType === userType;
  const isAI = message.senderType === 'ai';
  const canReport = chatId && !isAI && message.senderType !== 'ai' && message._id;
  
  // Get avatar source based on sender type
  const getAvatarSrc = () => {
    if (isAI) {
      // Use Lumi image from public folder
      return '/img/lumi.png';
    }
    
    if (message.senderType === 'supplier' && chatContext?.business) {
      // Show business logo for supplier messages
      return getAvatarSrcUtil(chatContext.business.media?.logo);
    }
    
    // For couple messages, use sender's profile picture (not wedding profile picture)
    if (message.senderType === 'couple') {
      return getAvatarSrcUtil(message.sender?.profilePicture);
    }
    
    // Fallback to sender's profile picture if available
    return getAvatarSrcUtil(message.sender?.profilePicture);
  };
  
  // Get avatar initials/fallback
  const getAvatarFallback = () => {
    if (isAI) {
      return 'L';
    }
    
    if (message.senderType === 'supplier' && chatContext?.business) {
      return chatContext.business.name?.[0]?.toUpperCase() || 'S';
    }
    
    // For couples, use sender's name initials
    if (message.sender?.firstName) {
      return message.sender.firstName[0].toUpperCase();
    }
    
    return '?';
  };
  
  // Determine bubble style
  let bubbleStyle = chatTheme.messageBubble.received;
  if (isSent) {
    bubbleStyle = chatTheme.messageBubble.sent;
  } else if (isAI) {
    bubbleStyle = chatTheme.messageBubble.ai;
  }
  
  // Format timestamp
  const timestamp = message.timestamp 
    ? formatDistanceToNow(new Date(message.timestamp), { addSuffix: true })
    : 'Just now';
  
  const avatarSrc = getAvatarSrc();
  const avatarFallback = getAvatarFallback();
  
  // Get sender name for display
  const getSenderName = () => {
    if (isAI) {
      return 'Lumi';
    }
    
    if (message.senderType === 'supplier' && chatContext?.business) {
      return chatContext.business.name || 'Supplier';
    }
    
    if (message.senderType === 'couple' && message.sender) {
      const firstName = message.sender.firstName || '';
      const lastName = message.sender.lastName || '';
      return `${firstName} ${lastName}`.trim() || message.sender.name || 'Couple';
    }
    
    // Fallback
    if (message.sender?.firstName) {
      return `${message.sender.firstName} ${message.sender.lastName || ''}`.trim() || message.sender.name || 'User';
    }
    
    return isSent ? currentUserName : 'User';
  };
  
  const senderName = getSenderName();
  
  // Check if message is a file attachment
  const parseFileAttachment = (content) => {
    try {
      const parsed = JSON.parse(content);
      if (parsed && parsed.type === 'file' && parsed.fileId && parsed.fileName && parsed.fileUrl) {
        return parsed;
      }
    } catch (e) {
      // Not a JSON file attachment, return null
    }
    return null;
  };
  
  const fileAttachment = parseFileAttachment(message.content) || (message.attachment?.url || message.attachment?.invoiceId ? {
    type: 'file',
    fileUrl: message.attachment?.url,
    fileName: message.attachment.fileName || 'attachment',
    fileType: message.attachment.fileType,
    invoiceId: message.attachment?.invoiceId
  } : null);
  const isTempMessage = message._id?.toString().startsWith('temp-');
  const [downloading, setDownloading] = useState(false);

  const handleDownloadFile = async (att) => {
    if (isTempMessage) return;
    if (att.invoiceId) {
      setDownloading(true);
      try {
        const response = await api.get(`/api/invoices/${att.invoiceId}/pdf`, { responseType: 'blob' });
        const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = att.fileName || 'invoice.pdf';
        link.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        // Error handled by api interceptor
      } finally {
        setDownloading(false);
      }
    } else if (att.fileUrl) {
      window.open(att.fileUrl, '_blank');
    }
  };

  const handleReportClick = (e) => {
    e.stopPropagation();
    setReportModalOpen(true);
  };

  const handleReportConfirm = async () => {
    if (!chatId || !message._id) return;
    setReporting(true);
    try {
      await api.post(`/api/chat/conversations/${chatId}/messages/${message._id}/report`);
      setReportModalOpen(false);
    } catch (err) {
      // Error handled by api interceptor; modal stays open
    } finally {
      setReporting(false);
    }
  };
  
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-end',
        justifyContent: isSent ? 'flex-end' : 'flex-start',
        mb: 2,
        gap: 1,
      }}
    >
      {/* Avatar - only show for received messages */}
      {!isSent && (
        <Avatar
          src={avatarSrc}
          sx={{
            width: 32,
            height: 32,
            bgcolor: '#ffffff',
            color: theme.palette.text.primary,
            border: '1px solid rgba(0, 0, 0, 0.1)',
            flexShrink: 0,
          }}
        >
          {!avatarSrc && avatarFallback}
        </Avatar>
      )}
      
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isSent ? 'flex-end' : 'flex-start',
          maxWidth: { xs: '85%', sm: '75%', md: '70%' },
          minWidth: '120px',
        }}
      >
        {/* Sender name above message for suppliers viewing couple messages */}
        {!isSent && message.senderType === 'couple' && message.sender && userType === 'supplier' && (
          <Typography
            variant="caption"
            sx={{
              color: '#666',
              fontSize: '0.75rem',
              fontWeight: 500,
              mb: 0.5,
              ml: 1,
            }}
          >
            {senderName}
          </Typography>
        )}
        <Box
          sx={{
            ...bubbleStyle,
            position: 'relative',
            maxWidth: '100%', // Override chatTheme maxWidth, let parent Box control width
            alignSelf: 'unset', // Override chatTheme alignSelf since we're controlling alignment in parent
          }}
        >
          {/* AI indicator */}
          {isAI && (
            <Chip
              label="Lumi"
              size="small"
              sx={{
                position: 'absolute',
                top: '-8px',
                left: '12px',
                height: '20px',
                fontSize: '0.7rem',
                backgroundColor: '#ef4192',
                color: '#fff',
                fontWeight: 500,
              }}
            />
          )}
          
          {/* Message content - show text unless content is JSON file attachment */}
          {message.isAutomated && (
            <Chip
              label="Sent automatically"
              size="small"
              sx={{
                mb: 0.75,
                height: 20,
                fontSize: '0.68rem',
                backgroundColor: isSent ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.08)',
                color: isSent ? '#fff' : '#455a64',
              }}
            />
          )}
          {message.content && !parseFileAttachment(message.content) && (
            <Typography
              variant="body1"
              sx={{
                color: isSent ? '#fff' : '#2C3E50',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                mb: fileAttachment ? 1 : 0.5,
              }}
            >
              {message.content}
            </Typography>
          )}
          {fileAttachment ? (
            <Card
              sx={{
                backgroundColor: isSent ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.02)',
                boxShadow: 'none',
                border: isSent ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.1)',
                borderRadius: 2,
                mb: 0.5,
                cursor: isTempMessage ? 'default' : 'pointer',
                opacity: isTempMessage ? 0.7 : 1,
                position: 'relative',
                '&:hover': {
                  backgroundColor: isTempMessage 
                    ? (isSent ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.02)')
                    : (isSent ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.05)'),
                },
              }}
              onClick={() => !isTempMessage && handleDownloadFile(fileAttachment)}
            >
              {isTempMessage && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    borderRadius: 2,
                    zIndex: 1,
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              )}
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: isSent ? '#fff' : '#2C3E50',
                        mb: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {fileAttachment.fileName}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: isSent ? 'rgba(255, 255, 255, 0.7)' : '#9e9e9e',
                        fontSize: '0.7rem',
                      }}
                    >
                      {isTempMessage ? 'Sending...' : downloading ? 'Downloading...' : 'Click to download'}
                    </Typography>
                  </Box>
                  {!isTempMessage && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadFile(fileAttachment);
                      }}
                      disabled={downloading}
                      sx={{
                        color: isSent ? '#fff' : 'primary.main',
                        '&:hover': {
                          backgroundColor: isSent ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                        },
                      }}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </CardContent>
            </Card>
          ) : null}
          
          {/* Timestamp with sender name and Report button */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
            <Typography
              variant="caption"
              sx={{
                color: isSent ? 'rgba(255, 255, 255, 0.7)' : '#9e9e9e',
                fontSize: '0.7rem',
              }}
            >
              {timestamp}
            </Typography>
            {senderName && (
              <>
                <Typography
                  variant="caption"
                  sx={{
                    color: isSent ? 'rgba(255, 255, 255, 0.7)' : '#9e9e9e',
                    fontSize: '0.7rem',
                  }}
                >
                  •
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: isSent ? 'rgba(255, 255, 255, 0.7)' : '#9e9e9e',
                    fontSize: '0.7rem',
                  }}
                >
                  {senderName}
                </Typography>
              </>
            )}
            {canReport && (
              <IconButton
                size="small"
                onClick={handleReportClick}
                sx={{
                  ml: 0.5,
                  p: 0.25,
                  color: isSent ? 'rgba(255, 255, 255, 0.5)' : '#9e9e9e',
                  '&:hover': {
                    color: isSent ? 'rgba(255, 255, 255, 0.8)' : 'error.main',
                  },
                }}
                title="Report message"
              >
                <FlagIcon sx={{ fontSize: 14 }} />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Report confirmation modal */}
        <Dialog open={reportModalOpen} onClose={() => !reporting && setReportModalOpen(false)}>
          <DialogTitle>Report message</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to report this message? It will be sent to our team for review.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReportModalOpen(false)} disabled={reporting}>
              Cancel
            </Button>
            <Button onClick={handleReportConfirm} color="error" variant="contained" disabled={reporting}>
              {reporting ? 'Reporting...' : 'Report'}
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Function call result indicator */}
        {message.functionCall && (
          <Box
            sx={{
              mt: 0.5,
              px: 1,
              py: 0.5,
              backgroundColor: 'rgba(76, 175, 80, 0.1)',
              borderRadius: '8px',
              fontSize: '0.75rem',
              color: '#4CAF50',
            }}
          >
            ✓ {message.functionCall.functionName} executed
          </Box>
        )}
      </Box>
      
      {/* Avatar for sent messages */}
      {isSent && (
        <Avatar
          src={
            // For couples, prefer message.sender.profilePicture (from backend), fallback to chatContext
            userType === 'couple' && message.sender?.profilePicture
              ? getAvatarSrcUtil(message.sender.profilePicture)
              : userType === 'couple' && chatContext?.user?.profilePicture
              ? getAvatarSrcUtil(chatContext.user.profilePicture)
              : userType === 'supplier' && chatContext?.business?.media?.logo
              ? getAvatarSrcUtil(chatContext.business.media.logo)
              : DEFAULT_AVATAR
          }
          sx={{
            width: 32,
            height: 32,
            bgcolor: '#ffffff',
            color: theme.palette.text.primary,
            border: '1px solid rgba(0, 0, 0, 0.1)',
            flexShrink: 0,
          }}
        >
          {userType === 'couple' && (chatContext?.user?.firstName?.[0]?.toUpperCase() || chatContext?.wedding?.name?.[0]?.toUpperCase())}
          {userType === 'supplier' && chatContext?.business?.name?.[0]?.toUpperCase()}
        </Avatar>
      )}
    </Box>
  );
};

export default MessageBubble;

